import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { LogoSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { AUTH_EVENTS, clearAuthPending } from '../../../utils/authFunnel';
import BackgroundBlobs from '../../_molecules/BackgroundBlobs/BackgroundBlobs';
import AuthForm, { AuthMode } from './AuthForm';
import AuthLangSwitcher from './AuthLangSwitcher';
import { rise, shadowCard } from './authStyles';
import JoinCodeView from './JoinCodeView';
import { useAuthStrings } from './useAuthStrings';

type Props = {
  initialMode: AuthMode;
  callbackUrl: string;
  isNorway?: boolean;
};

/**
 * Centered-layout login/signup experience: a single card over the floating
 * background blobs, with the language switcher pill (top-right). Flag-gated;
 * mounted by /login and /signup.
 */
const AuthExperience: React.FC<Props> = ({ initialMode, callbackUrl, isNorway }) => {
  const t = useAuthStrings();
  const metrics = useMetrics(true);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [view, setView] = useState<'auth' | 'join'>('auth');

  // Top of the funnel: the user reached the auth screen. Fire once per mount.
  // Reaching the auth screen also means any earlier attempt was abandoned/
  // cancelled, so drop a stale pending marker before a fresh choice sets a new
  // one — otherwise a later success could be logged with the wrong method.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    clearAuthPending();
    metrics.logEvent(AUTH_EVENTS.started, { mode: initialMode });
  }, [metrics, initialMode]);

  return (
    <Root>
      {/* Join-with-PIN is hidden for now (unvalidated). The JoinPill styled
          component, `view` state, and JoinCodeView branch are kept so it can be
          restored without rework. */}
      <CornerTR>
        <AuthLangSwitcher />
      </CornerTR>

      <Pane>
        <BackgroundBlobs zIndex={0} />
        <Card key={view + mode}>
          <LogoWrap>
            <LogoSvg />
          </LogoWrap>
          {view === 'join' ? (
            <JoinCodeView t={t} onBack={() => setView('auth')} />
          ) : (
            <AuthForm
              t={t}
              mode={mode}
              setMode={setMode}
              callbackUrl={callbackUrl}
              isNorway={isNorway}
            />
          )}
        </Card>
      </Pane>
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
`;

const Pane = styled.section`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${theme.colors.background};
  padding: 64px 20px;

  @media (max-width: 900px) {
    padding: 64px 18px 40px;
  }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 432px;
  background: #fff;
  padding: 44px 40px 36px;
  border-radius: 18px;
  box-shadow: ${shadowCard};
  animation: ${rise} 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);

  @media (max-width: 900px) {
    padding: 38px 26px 30px;
  }
`;

const LogoWrap = styled.div`
  display: block;
  width: max-content;
  margin: 0 auto 26px;
  color: ${theme.colors.orange};

  svg {
    height: 30px;
    width: auto;
    display: block;
  }
`;

const CornerTR = styled.div`
  position: absolute;
  top: 26px;
  right: clamp(24px, 4vw, 40px);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 8;

  @media (max-width: 900px) {
    top: 18px;
    right: 16px;
  }
`;

export default AuthExperience;
